import { FirebaseError } from 'firebase/app';

// https://firebase.google.com/docs/auth/admin/errors

export const handleFirebaseError = (error: FirebaseError): never => {
  switch (error?.code) {
    case 'auth/invalid-email':
      throw new Error('Invalid email address. Please enter a valid email.');
    case 'auth/user-not-found':
      throw new Error('User not found. Please check the email address.');
    case 'auth/wrong-password':
      throw new Error('Incorrect password. Please try again.');
    case 'auth/email-already-in-use':
      throw new Error('Email already in use. Please try another email.');
    case 'auth/weak-password':
      throw new Error('Password should be at least 6 characters.');
    case 'auth/operation-not-allowed':
      throw new Error('Operation not allowed. Please try again later.');
    case 'auth/invalid-verification-code':
      throw new Error('Invalid verification code. Please try again.');
    case 'auth/invalid-verification-id':
      throw new Error('Invalid verification ID. Please try again.');
    case 'auth/code-expired':
      throw new Error('Code expired. Please try again.');
    case 'auth/invalid-action-code':
      throw new Error('Invalid action code. Please try again.');
    case 'auth/user-disabled':
      throw new Error('User disabled. Please contact support.');
    case 'auth/invalid-credential':
      throw new Error('Invalid email or password. Please try again.');
    case 'auth/invalid-continue-uri':
      throw new Error('Invalid continue URL. Please try again.');
    case 'auth/unauthorized-continue-uri':
      throw new Error('Unauthorized continue URL. Please try again.');
    case 'auth/missing-continue-uri':
      throw new Error('Missing continue URL. Please try again.');
    case 'auth/missing-verification-code':
      throw new Error('Missing verification code. Please try again.');
    case 'auth/missing-verification-id':
      throw new Error('Missing verification ID. Please try again.');
    case 'auth/captcha-check-failed':
      throw new Error('Captcha check failed. Please try again.');
    case 'auth/invalid-phone-number':
      throw new Error('Invalid phone number. Please try again.');
    case 'auth/missing-phone-number':
      throw new Error('Missing phone number. Please try again.');
    case 'auth/quota-exceeded':
      throw new Error('Quota exceeded. Please try again.');
    case 'auth/missing-app-credential':
      throw new Error('Missing app credential. Please try again.');
    case 'auth/invalid-app-credential':
      throw new Error('Invalid app credential. Please try again.');
    case 'auth/session-expired':
      throw new Error('Session expired. Please try again.');
    case 'auth/missing-or-invalid-nonce':
      throw new Error('Missing or invalid nonce. Please try again.');
    case 'auth/missing-client-identifier':
      throw new Error('Missing client identifier. Please try again.');
    case 'auth/key-retrieval-failed':
      throw new Error('Key retrieval failed. Please try again.');
    case 'auth/invalid-oauth-provider':
      throw new Error('Invalid OAuth provider. Please try again.');
    case 'auth/invalid-oauth-client-id':
      throw new Error('Invalid OAuth client ID. Please try again.');
    case 'auth/invalid-cert-hash':
      throw new Error('Invalid cert hash. Please try again.');
    case 'auth/invalid-user-token':
      throw new Error('Invalid user token. Please try again.');
    case 'auth/invalid-custom-token':
      throw new Error('Invalid custom token. Please try again.');
    case 'auth/app-deleted':
      throw new Error('App deleted. Please try again.');
    case 'auth/app-not-authorized':
      throw new Error('App not authorized. Please try again.');
    case 'auth/argument-error':
      throw new Error('Argument error. Please try again.');
    case 'auth/invalid-api-key':
      throw new Error('Invalid API key. Please try again.');
    case 'auth/network-request-failed':
      throw new Error('Network request failed. Please try again.');
    case 'auth/requires-recent-login':
      throw new Error('Requires recent login. Please try again.');
    case 'auth/too-many-requests':
      throw new Error('Too many requests. Please try again.');
    case 'auth/unauthorized-domain':
      throw new Error('Unauthorized domain. Please try again.');
    case 'auth/user-token-expired':
      throw new Error('User token expired. Please try again.');
    case 'auth/web-storage-unsupported':
      throw new Error('Web storage unsupported. Please try again.');
    case 'auth/account-exists-with-different-credential':
      throw new Error(
        'Account exists with different credential. Please try again.'
      );
    case 'auth/auth-domain-config-required':
      throw new Error('Auth domain config required. Please try again.');
    case 'auth/cancelled-popup-request':
      throw new Error('Cancelled popup request. Please try again.');
    case 'auth/credential-already-in-use':
      throw new Error('Credential already in use. Please try again.');
    case 'auth/custom-token-mismatch':
      throw new Error('Custom token mismatch. Please try again.');
    case 'auth/provider-already-linked':
      throw new Error('Provider already linked. Please try again.');
    case 'auth/timeout':
      throw new Error('Timeout. Please try again.');
    case 'auth/missing-android-pkg-name':
      throw new Error('Missing Android package name. Please try again.');
    case 'auth/missing-ios-bundle-id':
      throw new Error('Missing iOS bundle ID. Please try again.');
    case 'auth/invalid-dynamic-link-domain':
      throw new Error('Invalid dynamic link domain. Please try again.');
    case 'auth/invalid-persistence-type':
      throw new Error('Invalid persistence type. Please try again.');
    case 'auth/unsupported-persistence-type':
      throw new Error('Unsupported persistence type. Please try again.');
    case 'auth/invalid-oauth-client-secret':
      throw new Error('Invalid OAuth client secret. Please try again.');
    case 'auth/invalid-argument':
      throw new Error('Invalid argument. Please try again.');
    case 'auth/invalid-creation-time':
      throw new Error('Invalid creation time. Please try again.');
    case 'auth/invalid-disabled-field':
      throw new Error('Invalid disabled field. Please try again.');
    case 'auth/invalid-display-name':
      throw new Error('Invalid display name. Please try again.');
    case 'auth/invalid-email-verified':
      throw new Error('Invalid email verified. Please try again.');
    case 'auth/invalid-hash-algorithm':
      throw new Error('Invalid hash algorithm. Please try again.');
    case 'auth/invalid-hash-block-size':
      throw new Error('Invalid hash block size. Please try again.');
    case 'auth/invalid-hash-derived-key-length':
      throw new Error('Invalid hash derived key length. Please try again.');
    case 'auth/invalid-hash-key':
      throw new Error('Invalid hash key. Please try again.');
    case 'auth/invalid-hash-memory-cost':
      throw new Error('Invalid hash memory cost. Please try again.');
    case 'auth/invalid-hash-parallelization':
      throw new Error('Invalid hash parallelization. Please try again.');
    case 'auth/invalid-hash-rounds':
      throw new Error('Invalid hash rounds. Please try again.');
    case 'auth/invalid-hash-salt-separator':
      throw new Error('Invalid hash salt separator. Please try again.');
    case 'auth/invalid-id-token':
      throw new Error('Invalid ID token. Please try again.');
    case 'auth/invalid-last-sign-in-time':
      throw new Error('Invalid last sign in time. Please try again.');
    case 'auth/invalid-page-token':
      throw new Error('Invalid page token. Please try again.');
    case 'auth/invalid-password':
      throw new Error('Invalid password. Please try again.');
    case 'auth/invalid-password-hash':
      throw new Error('Invalid password hash. Please try again.');
    case 'auth/invalid-password-salt':
      throw new Error('Invalid password salt. Please try again.');
    case 'auth/invalid-photo-url':
      throw new Error('Invalid photo URL. Please try again.');
    case 'auth/invalid-provider-id':
      throw new Error('Invalid provider ID. Please try again.');
    case 'auth/invalid-session-cookie-duration':
      throw new Error('Invalid session cookie duration. Please try again.');
    case 'auth/invalid-uid':
      throw new Error('Invalid UID. Please try again.');
    case 'auth/invalid-user-import':
      throw new Error('Invalid user import. Please try again.');
    case 'auth/invalid-provider-data':
      throw new Error('Invalid provider data. Please try again.');
    case 'auth/maximum-user-count-exceeded':
      throw new Error('Maximum user count exceeded. Please try again.');
    case 'auth/missing-hash-algorithm':
      throw new Error('Missing hash algorithm. Please try again.');
    case 'auth/missing-uid':
      throw new Error('Missing UID. Please try again.');
    case 'auth/reserved-claims':
      throw new Error('Reserved claims. Please try again.');
    case 'auth/session-cookie-revoked':
      throw new Error('Session cookie revoked. Please try again.');
    case 'auth/uid-already-exists':
      throw new Error('UID already exists. Please try again.');
    case 'auth/email-already-exists':
      throw new Error('Email already exists. Please try again.');
      break;
    case 'auth/phone-number-already-exists':
      throw new Error('Phone number already exists. Please try again.');
    case 'auth/project-not-found':
      throw new Error('Project not found. Please try again.');
    case 'auth/insufficient-permission':
      throw new Error('Insufficient permission. Please try again.');
    case 'auth/internal-error':
      throw new Error('Internal error. Please try again.');
    default:
      throw new Error('Oops! Something went wrong. Please try again later.');
  }
};

export function getFriendlyFirebaseMessage(error: any): string {
  const message = error?.error?.message || error?.message;

  switch (message) {
    case 'EMAIL_EXISTS':
      return 'This email is already registered. Trying to log you in...';
    case 'EMAIL_NOT_FOUND':
      return 'No user found with this email.';
    case 'INVALID_PASSWORD':
      return 'Incorrect password. Please try again.';
    case 'INVALID_LOGIN_CREDENTIALS':
      return 'Invalid email or password.';
    case 'USER_DISABLED':
      return 'This account has been disabled.';
    case 'TOO_MANY_ATTEMPTS_TRY_LATER':
      return 'Too many login attempts. Please try again later.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
}
